import { RadioCard, RadioCardProps } from '@almbrand/radiocard';
import { ThemeUnion } from '@almbrand/systemicons/dist/cjs/types/icon';
import classNames from 'classnames';
import { PageContext } from 'context/PageContext';
import { memo, useContext, useEffect, useMemo } from 'react';
import { FieldValues, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setCurrentRadioState } from 'store/slices/radioCardSlice';
import { store } from 'store/store';
import styles from './RadioCardComponent.module.scss';

export interface RadioCardComponentProps extends RadioCardProps<ThemeUnion> {
	index: number;
	register: UseFormRegister<FieldValues>;
	contentType: string;
	stepContentId: number;
	submitStep?: () => void;
	cardImage?: Image;
	cardImageChecked?: Image;
	imageFit?: 'contain' | 'cover';
	hasError?: boolean;
	trigger?: UseFormTrigger<FieldValues>;
	stepTitle?: string;
	setValue?: UseFormSetValue<FieldValues>;
}

export const RadioCardComponent: React.FC<RadioCardComponentProps> = memo(
	({
		index,
		register,
		submitStep,
		name,
		contentType,
		stepContentId,
		cardImage,
		cardImageChecked,
		imageFit,
		hasError,
		trigger,
		setValue,
		...props
	}) => {
		const pageContext = useContext(PageContext);
		const { theme } = pageContext ?? {};
		const { displayGlobalError } = useAppSelector((state) => state.error);
		const isChecked = useAppSelector((state) =>
			state.currentRadioState.some((radioState) => radioState.name === name && radioState.index === index)
		);
		const dispatch = useAppDispatch();
		const { onChange: onRadioChange, name: radioName } = register(stepContentId.toString(), { required: true });

		const updatedComponentProps = {
			...props,
			errorMessage: undefined,
		};

		const handleOnChange = () => {
			dispatch(setCurrentRadioState({ index, name }));
			onRadioChange({
				target: {
					name: radioName,
					value: {
						id: name,
						value: props.value,
						label: props.title,
						index: index,
						type: contentType,
						stepTitle: props.stepTitle,
					},
				},
			});
			trigger(name);
			submitStep?.();
		};

		const radioImages = useMemo(() => {
			if (!cardImage?.url) return props.image;

			return [cardImage, cardImageChecked]
				.filter((image): image is Image => Boolean(image?.url))
				.map((image) => ({
					url: image.url,
					fit: imageFit,
					alt: image.properties?.altText,
				}));
		}, [cardImage, cardImageChecked, imageFit, props.image]);

		useEffect(() => {
			const value = store.getState().calculatorForm[name]?.data?.value;
			if (value && props.value === value) {
				dispatch(setCurrentRadioState({ index, name }));
				setValue(name, {
					id: name,
					value: props.value,
					label: props.title,
					index: index,
					type: contentType,
					stepTitle: props.stepTitle,
				});
				trigger(name);
			}
		}, []);
		return (
			<div
				className={classNames(
					styles.RadioCardComponent,
					hasError && styles.RadioCardComponent__error,
					(isChecked || props.checked) && styles.RadioCardComponent__checked_border
				)}
				data-horizontal={cardImage?.url && 'true'}
			>
				<RadioCard
					errorMessage={displayGlobalError ? undefined : props.errorMessage}
					{...updatedComponentProps}
					image={radioImages}
					themeName={theme}
					checked={isChecked || props.checked}
					onChange={handleOnChange}
					backgroundColor='#FFFFFF'
					hasError={hasError}
					name={name}
				/>
			</div>
		);
	}
);

RadioCardComponent.displayName = 'RadioCardComponent';
